import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { getStorage } from '../firebase';
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE_BYTES } from '../utils/constants';

/**
 * Validate a file/blob against the shared allowed-type and size limits.
 * Mirrors validateFile in the web DocumentUpload component so messages,
 * activities, and the document vault all enforce the same constraints.
 *
 * @param {File|Blob} file - browser File (web) or Blob (mobile)
 * @param {{ fileName?: string, fileType?: string, fileSize?: number }} [meta]
 *   Explicit metadata for React Native blobs, which may lack .name/.type.
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateAttachment = (file, meta = {}) => {
  const fileType = meta.fileType || file?.type || '';
  const fileSize = meta.fileSize != null ? meta.fileSize : file?.size;

  if (!ALLOWED_FILE_TYPES[fileType]) {
    return { valid: false, error: 'This file type is not supported.' };
  }
  if (fileSize != null && fileSize > MAX_FILE_SIZE_BYTES) {
    const maxMb = Math.round(MAX_FILE_SIZE_BYTES / (1024 * 1024));
    return { valid: false, error: `Files must be ${maxMb}MB or smaller.` };
  }
  return { valid: true };
};

/**
 * Upload a file to a caller-supplied Storage path and return its metadata.
 * Pure Storage upload — the caller owns where the file lives and attaches
 * the returned metadata to its own Firestore doc (message/activity).
 * Modeled on uploadReceipt() in expenseService.
 *
 * @param {string} storagePath - full Storage path (caller decides ownership)
 * @param {File|Blob} file
 * @param {{ fileName?: string, fileType?: string, fileSize?: number, onProgress?: (pct:number)=>void }} [options]
 *   fileName is required for React Native blobs (no .name); onProgress reports 0-100.
 * @returns {Promise<{ url, storagePath, fileName, fileType, fileSize }>}
 */
export const uploadAttachment = (storagePath, file, options = {}) => {
  const storage = getStorage();
  const fileName = options.fileName || file.name;
  const fileType = options.fileType || file.type || '';
  const fileSize = options.fileSize != null ? options.fileSize : file.size;
  const onProgress = options.onProgress;
  const storageRef = ref(storage, storagePath);

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, file);
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (onProgress) {
          onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
        }
      },
      (error) => reject(error),
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({ url, storagePath, fileName, fileType, fileSize });
        } catch (error) {
          reject(error);
        }
      }
    );
  });
};

/**
 * Build the attachment path for a message thread.
 * @param {string} familyId
 * @param {string} threadId
 * @param {string} fileName
 */
export const threadAttachmentPath = (familyId, threadId, fileName) =>
  `families/${familyId}/threads/${threadId}/attachments/${Date.now()}_${fileName}`;

/**
 * Build the attachment path for an activity.
 */
export const activityAttachmentPath = (familyId, activityId, fileName) =>
  `families/${familyId}/activities/${activityId}/attachments/${Date.now()}_${fileName}`;

/**
 * Build a Storage path under the Documents vault for a "save to Documents"
 * attachment. Any subpath under documents/ inherits the existing Storage rule.
 */
export const documentUploadPath = (familyId, fileName) =>
  `families/${familyId}/documents/${Date.now()}_${fileName}`;

/**
 * Normalize a Documents-vault record into the attachment shape stored on
 * messages/activities. Used both when a picked/uploaded file is saved to the
 * vault and when an existing vault file is chosen. The documentId reference
 * lets the UI degrade gracefully if the vault file is later deleted.
 *
 * @param {object} document - a document record from documentService
 * @returns {{ url, storagePath, fileName, fileType, fileSize, documentId }}
 */
export const documentToAttachment = (document) => ({
  url: document.storageUrl,
  storagePath: document.storagePath,
  fileName: document.originalFileName || document.name,
  fileType: document.fileType || '',
  fileSize: document.fileSize,
  documentId: document.id,
});

/**
 * Delete an inline (non-vault) attachment's Storage object. No-op for
 * vault-backed attachments (documentId present) — those are owned by the
 * Documents vault and removed through deleteDocument instead.
 *
 * @param {{ storagePath?: string, documentId?: string }} attachment
 */
export const deleteAttachmentFile = async (attachment) => {
  if (!attachment?.storagePath || attachment.documentId) return;
  const storage = getStorage();
  try {
    await deleteObject(ref(storage, attachment.storagePath));
  } catch (error) {
    console.warn('Attachment file already deleted or not found:', error);
  }
};
